import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Undo2, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { returnsSettingsService } from '@/services/platform/returns-settings';

/**
 * Settings → Returns policy.
 *
 * Single tenant-level switch: when does a return settle the customer?
 *   • On warehouse receipt + QC (default) — refund / exchange is issued
 *     only after the goods physically arrive and pass inspection.
 *   • On carrier pickup — settle the moment the reverse-pickup carrier
 *     confirms it has collected the parcel.
 *
 * Pickup-time settlement exists for tenants on slow or unreliable
 * international reverse logistics, where waiting weeks for the parcel
 * to clear customs and reach the warehouse is a poor customer
 * experience. The trade-off is the tenant absorbs the QC risk — the
 * customer is always made whole on pickup. Enforced on the BE — see
 * `Tenant.settleReturnsOnPickup` and `ReturnsService.settleReturnOnPickup`.
 */
export function ReturnsSettingsPage() {
  const qc = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['tenant', 'returns-settings'],
    queryFn: returnsSettingsService.get,
  });

  const [onPickup, setOnPickup] = useState(false);
  useEffect(() => {
    if (settingsQuery.data) setOnPickup(settingsQuery.data.settleReturnsOnPickup);
  }, [settingsQuery.data]);

  const dirty =
    settingsQuery.data && onPickup !== settingsQuery.data.settleReturnsOnPickup;

  const mutation = useMutation({
    mutationFn: () =>
      returnsSettingsService.update({ settleReturnsOnPickup: onPickup }),
    onSuccess: () => {
      toast.success('Returns policy updated');
      qc.invalidateQueries({ queryKey: ['tenant', 'returns-settings'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not update'),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6 py-8">
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="grid h-12 w-12 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <Undo2 className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-[20px] font-semibold leading-tight tracking-tight">
              Returns policy
            </h1>
            <p className="text-[13px] text-muted-foreground">
              When a return is settled — refund issued or exchange replacement
              created. Affects new returns and those still in progress.
            </p>
          </div>
        </div>
      </header>

      <section className="space-y-3">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Settlement timing
        </div>
        <div className="space-y-2.5">
          <ModeOption
            value={false}
            current={onPickup}
            label="Settle on warehouse receipt"
            blurb="The refund or exchange is issued only after the returned goods physically arrive at the warehouse and pass QC. Standard, lowest-risk flow."
            example="Pickup → in transit → received → QC → settled"
            audience="Typical for domestic returns with fast, reliable reverse logistics."
            onSelect={() => setOnPickup(false)}
          />
          <ModeOption
            value={true}
            current={onPickup}
            label="Settle on carrier pickup"
            blurb="The customer is settled the moment the reverse-pickup carrier confirms collection — without waiting for the parcel to arrive or pass QC. The tenant absorbs the QC risk."
            example="Pickup confirmed → settled immediately"
            audience="For slow or unreliable international reverse logistics where waiting weeks hurts the customer experience."
            onSelect={() => setOnPickup(true)}
          />
        </div>
      </section>

      <div className="flex items-center justify-between pt-3">
        <div className="text-[11.5px] text-muted-foreground">
          {settingsQuery.data ? (
            <>
              Current saved policy:{' '}
              <Badge variant="muted">
                {settingsQuery.data.settleReturnsOnPickup
                  ? 'Settle on carrier pickup'
                  : 'Settle on warehouse receipt'}
              </Badge>
            </>
          ) : (
            'Loading…'
          )}
        </div>
        <Button
          size="sm"
          disabled={!dirty || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Saving…' : 'Save policy'}
        </Button>
      </div>

      <div className="rounded-md border border-info/20 bg-info/5 px-4 py-3 text-[12px] leading-relaxed text-muted-foreground">
        <strong className="text-foreground">Note:</strong> with pickup-time
        settlement, the customer is always made whole when the carrier collects
        the parcel — even if the goods later fail QC or never arrive. Use it only
        for routes where the reverse leg is genuinely slow. The setting does not
        apply to returns that are already closed.
      </div>
    </div>
  );
}

function ModeOption({
  value,
  current,
  label,
  blurb,
  example,
  audience,
  onSelect,
}: {
  value: boolean;
  current: boolean;
  label: string;
  blurb: string;
  example: string;
  audience: string;
  onSelect: () => void;
}) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-md border bg-card px-4 py-3.5 text-left transition-colors ${
        active
          ? 'border-primary/40 ring-1 ring-inset ring-primary/15'
          : 'border-border/70 hover:border-border'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 ${
            active ? 'border-primary bg-primary' : 'border-muted-foreground/40'
          }`}
        >
          {active && (
            <CheckCircle2 className="h-4 w-4 fill-primary text-primary-foreground -translate-x-px -translate-y-px" />
          )}
        </div>
        <div className="flex-1 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-semibold">{label}</span>
          </div>
          <p className="text-[12.5px] leading-relaxed text-muted-foreground">{blurb}</p>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Badge variant="muted" className="font-mono text-[10.5px]">
              {example}
            </Badge>
            <span className="text-[11px] text-muted-foreground">{audience}</span>
          </div>
        </div>
      </div>
    </button>
  );
}
